import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { Sidebar } from '@/components/sidebar/Sidebar';
import { ChatCanvas } from '@/components/canvas/ChatCanvas';
import { DocsPanel } from '@/components/docs/DocsPanel';
import { ReportsView } from '@/components/views/ReportsView';
import { WatchlistView } from '@/components/views/WatchlistView';
import { LoginModal } from '@/components/modals/LoginModal';
import { PlansModal } from '@/components/modals/PlansModal';
import { PrintReport } from '@/components/PrintReport';
import type { BotContentHandlers } from '@/components/bot/BotContent';
import type { Project } from '@/data/chatRepo';
import { collectCitations } from '@/lib/citations';
import { projectsQuery, queryKeys, watchlistQuery } from '@/data/queries';
import { useChat } from '@/lib/use-chat';
import { useChatRepo } from '@/lib/use-chat-repo';
import { useMediaQuery } from '@/lib/use-media-query';

export const Route = createFileRoute('/')({
  component: HomePage,
});

/**
 * Trust Agent home. Mounts the three-panel shell with the live sidebar (C4) and
 * the chat canvas + composer (C5). The documents panel is filled by C9; it
 * shows a placeholder for now. Chat state lives in the `useChat` controller.
 */
function HomePage() {
  const repo = useChatRepo();
  const queryClient = useQueryClient();
  const { data: projects = [] } = useQuery(projectsQuery(repo));
  const { data: watchlist = [] } = useQuery(watchlistQuery(repo));
  const chat = useChat();

  const [navOpen, setNavOpen] = useState(false);
  // Documents panel visibility: a right slide-over drawer ≤1000px (`docsOpen`),
  // an inline column on desktop that the header doc icon can collapse.
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsCollapsed, setDocsCollapsed] = useState(false);
  const docsIsDrawer = useMediaQuery('(max-width: 1000px)');
  /** Header doc icon: toggle the drawer on mobile, the inline panel on desktop. */
  const toggleDocs = (): void => {
    if (docsIsDrawer) setDocsOpen((o) => !o);
    else setDocsCollapsed((c) => !c);
  };
  /** Reveal the documents panel on whichever surface is active. */
  const revealDocs = (): void => {
    setDocsCollapsed(false);
    setDocsOpen(true);
  };
  /**
   * Surface the dossier without grabbing focus: un-collapse the inline desktop
   * panel, but never auto-pop the mobile drawer (it covers the chat the user
   * just opened). The header doc icon stays the explicit way in on mobile.
   */
  const offerDocs = (): void => {
    setDocsCollapsed(false);
    if (!docsIsDrawer) setDocsOpen(true);
  };
  const docsActive = docsIsDrawer ? docsOpen : !docsCollapsed;
  const [view, setView] = useState<'chat' | 'reports' | 'watchlist'>('chat');
  const [activeTab, setActiveTab] = useState('chat');
  // Global number of the source to highlight in the "Nguồn & trích dẫn" tab,
  // set when a `[n]` citation marker is clicked in a chat answer.
  const [sourceHighlight, setSourceHighlight] = useState<number | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [user, setUser] = useState<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [printProject, setPrintProject] = useState<Project | null>(null);

  const getProject = useMemo(
    () => (id: string) => projects.find((p) => p.id === id),
    [projects],
  );

  // Aggregate every citation in the active conversation for the "Nguồn & trích
  // dẫn" tab, and the per-message global numbers used by the inline `[n]` markers.
  const citations = useMemo(
    () => collectCitations(chat.messages, getProject),
    [chat.messages, getProject],
  );

  // Clicking a `[n]` marker in chat jumps to the sources tab and highlights it.
  const onCiteClick = (n: number): void => {
    setActiveTab('sources');
    setSourceHighlight(n);
  };

  // Reset the highlight when the conversation changes so a stale source from a
  // previous thread isn't highlighted.
  useEffect(() => {
    setSourceHighlight(null);
  }, [chat.activeId]);

  /**
   * Print/"Tải PDF" mock (demo `doPrint`): mount the off-screen report for the
   * project, then open the browser print dialog on the next frame so the DOM
   * has painted. The report prints as a standalone black-on-white document.
   */
  const printReport = (id: string): void => {
    const project = getProject(id);
    if (!project) return;
    setPrintProject(project);
    setTimeout(() => window.print(), 60);
  };

  const activeProject = chat.active?.pid ? getProject(chat.active.pid) : undefined;
  // The dossier follows an explicitly opened project, else the active thread's.
  const focusedProject =
    (focusedId ? getProject(focusedId) : undefined) ?? activeProject;
  const closeNav = () => setNavOpen(false);

  const openProject = (id: string): void => {
    chat.openProject(id);
    setFocusedId(id);
    setView('chat');
    revealDocs();
  };

  /** View a project's dossier only — reveal the docs panel without switching
   * the chat to (or creating) a project thread. */
  const viewProfile = (id: string): void => {
    setFocusedId(id);
    setView('chat');
    revealDocs();
  };

  const watchedIds = useMemo(
    () => new Set(watchlist.map((w) => w.id)),
    [watchlist],
  );
  const isWatched = (id: string): boolean => watchedIds.has(id);

  /**
   * Toggle a project's watchlist membership: add it if absent, remove it if
   * present, then refresh the watchlist query so the star fills/empties and the
   * sidebar mini-cards + WatchlistView update. Silent — the star state is the
   * feedback, so repeated toggles don't spam the chat.
   */
  const toggleWatch = (id: string): void => {
    if (!getProject(id)) return;
    const mutate = watchedIds.has(id)
      ? repo.removeFromWatchlist(id)
      : repo.addToWatchlist(id);
    void mutate.then((changed) => {
      if (changed) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.watchlist });
      }
    });
  };

  const handlers: BotContentHandlers = {
    onOpenProject: openProject,
    onViewProfile: viewProfile,
    onPdf: printReport,
    onPrint: printReport,
    onWatch: toggleWatch,
    onChip: (chip) => {
      const action = chip.action;
      if (action.startsWith('open:')) {
        openProject(action.slice('open:'.length));
        return;
      }
      // Project-scoped chips inside a project thread (e.g. "Pháp lý dự án này")
      // carry the id, so run the analysis directly instead of showing a picker.
      if (action.startsWith('check:')) {
        chat.completePicker('check', [action.slice('check:'.length)]);
        return;
      }
      if (action.startsWith('cashflow:')) {
        chat.completePicker('cashflow', [action.slice('cashflow:'.length)]);
        return;
      }
      chat.send(chip.label);
    },
    getProject,
    onWizardComplete: chat.completeWizard,
    onPickerSubmit: chat.completePicker,
    citationNumbers: citations.numbersByMessage,
    onCiteClick,
    isWatched,
  };

  const canvas =
    view === 'reports' ? (
      <ReportsView
        onOpenProject={openProject}
        onPdf={printReport}
        onNewReport={() => {
          chat.newChat();
          setView('chat');
        }}
        onOpenNav={() => setNavOpen(true)}
      />
    ) : view === 'watchlist' ? (
      <WatchlistView
        onOpenProject={openProject}
        onAddProject={() => {
          chat.newChat();
          setView('chat');
        }}
        onOpenNav={() => setNavOpen(true)}
      />
    ) : (
      <ChatCanvas
        title={activeProject ? activeProject.name : 'Trust Agent'}
        status={
          activeProject
            ? 'Trò chuyện về dự án · có trích dẫn'
            : 'Trả lời kèm trích dẫn nguồn · TrustLand AI'
        }
        hasProject={Boolean(activeProject)}
        messages={chat.messages}
        typing={chat.typing}
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        sources={citations.sources}
        sourceHighlight={sourceHighlight}
        onSend={chat.send}
        onOpenNav={() => setNavOpen(true)}
        onToggleDocs={toggleDocs}
        docsActive={docsActive}
        onNewProjectChat={() => {
          // The header "+" starts a fresh thread about the project in focus
          // (demo `newProjectChat`); without a project it's a plain new chat.
          if (activeProject) {
            chat.newProjectChat(activeProject.id);
            setFocusedId(activeProject.id);
            revealDocs();
          } else {
            chat.newChat();
          }
          setView('chat');
        }}
        onSearch={() => setView('watchlist')}
        handlers={handlers}
      />
    );

  return (
    <>
      <div className="trust-app contents">
      <AppShell
        showDocs={view === 'chat'}
        navOpen={navOpen}
        onNavOpenChange={setNavOpen}
        docsOpen={docsOpen}
        onDocsOpenChange={setDocsOpen}
        docsCollapsed={docsCollapsed}
        sidebar={
          <Sidebar
            view={view}
            conversations={chat.conversations}
            activeConversationId={chat.activeId}
            user={user}
            onSelectView={(v) => {
              if (v === 'reports' || v === 'watchlist') setView(v);
              else setView('chat');
              closeNav();
            }}
            onSelectConversation={(id) => {
              chat.setActiveId(id);
              setView('chat');
              // Demo `openConv`: a project thread reopens its dossier; a plain
              // thread clears the focused project.
              const conv = chat.conversations.find((c) => c.id === id);
              if (conv?.pid) {
                setFocusedId(conv.pid);
                // Surface the dossier inline on desktop, but don't auto-open the
                // mobile drawer over the conversation the user just opened.
                offerDocs();
              } else {
                setFocusedId(null);
                setDocsOpen(false);
              }
              closeNav();
            }}
            onOpenProject={(id) => {
              openProject(id);
              closeNav();
            }}
            onNewChat={() => {
              chat.newChat();
              setView('chat');
              closeNav();
            }}
            onLogin={() => setLoginOpen(true)}
            onUpgrade={() => setPlansOpen(true)}
          />
        }
        canvas={canvas}
        docs={
          <DocsPanel
            project={focusedProject}
            onPdf={(id) =>
              chat.send(`Xuất báo cáo PDF — ${getProject(id)?.name ?? ''}`)
            }
            onWatch={toggleWatch}
            watched={focusedProject ? isWatched(focusedProject.id) : false}
          />
        }
      />
      <LoginModal open={loginOpen} onOpenChange={setLoginOpen} onLogin={setUser} />
      <PlansModal
        open={plansOpen}
        onOpenChange={setPlansOpen}
        onChoose={() => setPlansOpen(false)}
      />
      </div>
      <PrintReport project={printProject} />
    </>
  );
}
